import { API_URL } from "env";
import { mixLogOut } from '../services/mixin';

/* ================= TYPES ================= */

/** Một trạng thái mà lịch hẹn được phép chuyển sang. */
export interface AppointmentNextTag {
    id: number;
    name: string;
    code?: string | null;
    color?: string | null;
    text?: string | null;
    status?: number | null;
    status_text?: string | null;
    customer_tag_status?: number | null;
    customer_tag_status_text?: string | null;
    description?: string | null;
}

export interface AppointmentChannelCustomer {
    id: number;
    channel_connection_id: number;
    external_user_id: string;
    display_name: string;
    avatar?: string | null;
    avatar_url: string | null;
    unread_count: number;
    last_message_text: string;
    first_seen_at: string;
    last_seen_at: string;
    metadata: any;
    created_at: string;
    updated_at: string;
    province_code: string | null;
    ward_code: string | null;
    staff_id: number | null;
    customer_tag_id: number | null;
    customer_occupation_id?: number | null;
    phone?: string | null;
    address?: string | null;
    customer_care_status_id?: number | null;
    note?: string | null;
    email?: string | null;
    birthday?: string | null;
    gender?: number;
    customer_tag_status?: number | null;
    customer_tag_status_text?: string | null;
    customer_tag?: { id: number; name: string; color?: string | null; text?: string | null; status_text?: string | null; description?: string | null; status?: number | null } | null;
    /** @deprecated Luồng tĩnh theo `next_ids` của thẻ. Backend sẽ bỏ, dùng `dynamic_next_tags`. */
    next_tag?: AppointmentNextTag[] | null;
    /** Các trạng thái được phép chuyển sang, backend tính theo ma trận phòng ban + màn hình. */
    dynamic_next_tags?: AppointmentNextTag[] | null;
}

export interface AppointmentService {
    id: number;
    name: string;
    pivot?: {
        appointment_id: number;
        service_id: number;
    };
}

export interface AppointmentStaff {
    id: number;
    fullname?: string;
    name?: string;
}

export interface AppointmentCreator {
    id: number;
    fullname: string;
}

/** Thẻ trạng thái khách gắn với lịch hẹn (dùng cho màu sắc + điều hướng trang chi tiết) */
export interface AppointmentTag {
    id: number;
    name: string;
    color?: string | null;
    description?: string | null;
    // 1 = lịch hẹn, 2 = lịch tư vấn, 3 = lịch thực hiện
    page?: number | null;
}

export interface AppointmentNote {
    id: number;
    appointment_id: number;
    staff_id?: number | null;
    content: string;
    created_at?: string;
    staff?: AppointmentStaff | null;
}

export interface AppointmentItem {
    id: number;
    code: string;
    branch_id: number | null;
    channel_customer_id: number | null;
    channel_connection_id: string | null;
    service_id: number | null;
    staff_id: number | null;
    note: string | null;
    status: number;
    scheduled_at: string;
    created_by_id: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    customer_name?: string;
    customer_phone?: string;
    customer_email?: string;
    customer_gender?: number;
    customer_dob?: string;
    /** Khách nước ngoài (0: trong nước, 1: nước ngoài) — quyết định Tỉnh/Phường có bắt buộc không */
    is_foreign?: number | null;
    customer_tag_status?: number | null;
    customer_tag_status_text?: string | null;
    // Màn hình lịch hẹn đang thuộc về, lấy từ `page` của thẻ trạng thái:
    // 1 = lịch hẹn, 2 = lịch tư vấn, 3 = lịch thực hiện (xem utils/appointmentRoute)
    customer_tag_page?: number | null;
    customer_tag_id?: number | null;
    customer_tag?: AppointmentTag | null;
    status_text?: string | null;
    notes?: AppointmentNote[] | null;
    customer?: any | null;
    channel_customer: AppointmentChannelCustomer | null;
    branch: any | null;
    service: AppointmentService | null;
    services: AppointmentService[];
    staff: AppointmentStaff | null;
    telesale_id?: number | null;
    telesale?: AppointmentStaff | null;
    branch_staff: { id?: number; name?: string; fullname?: string } | null;
    /** Bác sĩ / KTV thực hiện */
    action_staff?: { id?: number; name?: string; fullname?: string } | null;
    creator: AppointmentCreator | null;
    address?: string | null;
    province?: { code: string; name: string; full_name?: string } | null;
    ward?: { code: string; name: string; full_name?: string } | null;
    is_cared?: boolean;
    care_status_text?: string | null;
    /** @deprecated Luồng tĩnh theo `next_ids` của thẻ. Backend sẽ bỏ, dùng `dynamic_next_tags`. */
    next_tag?: AppointmentNextTag[] | null;
    /** Các trạng thái được phép chuyển sang, backend tính theo ma trận phòng ban + màn hình. */
    dynamic_next_tags?: AppointmentNextTag[] | null;
}

export interface AppointmentListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: AppointmentItem[];
    };
    msg: string;
}

export interface AppointmentDetailResponse {
    status: boolean;
    data: AppointmentItem | null;
    msg: string;
}

export interface AppointmentStatusSummaryItem {
    status: string | number;
    text?: string;
    count: number;
}

export interface AppointmentStatusSummaryResponse {
    status: boolean;
    data: AppointmentStatusSummaryItem[];
    msg?: string;
}

/* ================= API ================= */

export async function getAppointments(params?: {
    search?: string;
    status?: number;
    statuses?: number[];
    branch_id?: number;
    staff_id?: number;
    telesale_id?: number;
    staff_filter?: number[];
    customer_tag_status?: number;
    date_from?: string;
    date_to?: string;
    channel_connection_id?: number;
    channel_customer_id?: number;
    customer_id?: number;
    type_appointment?: number;
    page?: number;
    take?: number;
    limit?: number;
}): Promise<AppointmentListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.status !== undefined) query.append('status', String(params.status));
        if (params?.statuses?.length) params.statuses.forEach(s => query.append('status[]', String(s)));
        if (params?.branch_id !== undefined) query.append('branch_id', String(params.branch_id));
        if (params?.staff_id !== undefined) query.append('staff_id', String(params.staff_id));
        if (params?.telesale_id !== undefined) query.append('telesale_id', String(params.telesale_id));
        if (params?.staff_filter?.length) params.staff_filter.forEach(id => query.append('staff_filter[]', String(id)));
        if (params?.customer_tag_status !== undefined) query.append('customer_tag_status', String(params.customer_tag_status));
        if (params?.date_from) query.append('date_from', params.date_from);
        if (params?.date_to) query.append('date_to', params.date_to);
        if (params?.channel_connection_id !== undefined) query.append('channel_connection_id', String(params.channel_connection_id));
        if (params?.channel_customer_id !== undefined) query.append('channel_customer_id', String(params.channel_customer_id));
        if (params?.customer_id !== undefined) query.append('customer_id', String(params.customer_id));
        if (params?.type_appointment !== undefined) query.append('type_appointment', String(params.type_appointment));
        if (params?.page) query.append('page', String(params.page));
        if (params?.take) query.append('take', String(params.take));
        if (params?.limit) query.append('limit', String(params.limit));
        const qs = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/appointments${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        // Normalize: API may return data.data (array), map to data.items
        if (json.status && json.data && Array.isArray(json.data.data)) {
            json.data.items = json.data.data;
            delete json.data.data;
        }
        // Normalize channel_customer: rename status/status_text → customer_tag_status/customer_tag_status_text
        if (json.status && Array.isArray(json.data?.items)) {
            json.data.items = json.data.items.map((item: any) => {
                if (!item.channel_customer) return item;
                const cc = { ...item.channel_customer };
                if ('status' in cc && !('customer_tag_status' in cc)) {
                    cc.customer_tag_status = cc.status;
                    delete cc.status;
                }
                if ('status_text' in cc && !('customer_tag_status_text' in cc)) {
                    cc.customer_tag_status_text = cc.status_text;
                    delete cc.status_text;
                }
                return { ...item, channel_customer: cc };
            });
        }
        return json;
    } catch (error) {
        console.error('Failed to fetch appointments:', error);
        return { status: false, data: { total: 0, current_page: 1, items: [] }, msg: '' };
    }
}

export async function getAppointmentDetail(id: number): Promise<AppointmentDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/appointments/${id}/booking`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        return {
            status: Boolean(json?.status),
            data: json?.data || null,
            msg: json?.msg || '',
        };
    } catch (error) {
        console.error('Failed to fetch appointment detail:', error);
        return { status: false, data: null, msg: '' };
    }
}

export async function getAppointmentExecutionDetail(id: number): Promise<AppointmentDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/appointments/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        return {
            status: Boolean(json?.status),
            data: json?.data || null,
            msg: json?.msg || '',
        };
    } catch (error) {
        console.error('Failed to fetch appointment execution detail:', error);
        return { status: false, data: null, msg: '' };
    }
}

export async function getAppointmentConsultationDetail(id: number): Promise<AppointmentDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/appointments/${id}/consultation`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        return {
            status: Boolean(json?.status),
            data: json?.data || null,
            msg: json?.msg || '',
        };
    } catch (error) {
        console.error('Failed to fetch appointment consultation detail:', error);
        return { status: false, data: null, msg: '' };
    }
}

/* ---------- Dashboard: Top telesale / dịch vụ / lịch theo ngày ---------- */

export interface DashboardTelesaleItem {
    telesale_id: number;
    appointment_count: number;
    telesale: { id: number; name: string; avatar?: string | null } | null;
}

export interface DashboardServiceItem {
    service_id: number;
    service_name: string;
    service_code?: string | null;
    total_quantity: number;
}

export interface DashboardDailyItem {
    date: string;
    appointment_count: number;
}

export interface AppointmentDashboardResponse {
    status: boolean;
    data: {
        telesale_appointments: DashboardTelesaleItem[];
        service_appointments: DashboardServiceItem[];
        daily_appointments: DashboardDailyItem[];
    };
    msg?: string;
}

/**
 * Thống kê Top Telesale tạo lịch hẹn (lịch mới - status = 1), kèm tỷ trọng dịch vụ
 * và số lịch theo ngày. `date_from`/`date_to` lọc theo ngày hẹn `scheduled_at` và chỉ
 * ảnh hưởng tới telesale + dịch vụ; `daily_appointments` luôn trả 7 ngày gần nhất.
 */
export async function getAppointmentDashboard(params?: {
    date_from?: string;
    date_to?: string;
}): Promise<AppointmentDashboardResponse> {
    const empty = { telesale_appointments: [], service_appointments: [], daily_appointments: [] };
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.date_from) query.append('date_from', params.date_from);
        if (params?.date_to) query.append('date_to', params.date_to);
        const qs = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/appointments/dashboard/index${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        return {
            status: Boolean(json?.status),
            data: {
                telesale_appointments: Array.isArray(json?.data?.telesale_appointments) ? json.data.telesale_appointments : [],
                service_appointments: Array.isArray(json?.data?.service_appointments) ? json.data.service_appointments : [],
                daily_appointments: Array.isArray(json?.data?.daily_appointments) ? json.data.daily_appointments : [],
            },
            msg: json?.msg,
        };
    } catch (error) {
        console.error('Failed to fetch appointment dashboard:', error);
        return { status: false, data: empty };
    }
}

/**
 * Mỗi màn hình có tập trạng thái riêng:
 *  - appointment  → /summary/status              (đủ 5 trạng thái)
 *  - consultation → /summary/status-consultation (bỏ "Đặt lịch mới")
 *  - action       → /summary/status-action       (Hủy lịch, Chờ thực hiện, Đã thực hiện)
 */
export type AppointmentStatusScope = 'appointment' | 'consultation' | 'action';

const STATUS_SUMMARY_PATH: Record<AppointmentStatusScope, string> = {
    appointment: 'status',
    consultation: 'status-consultation',
    action: 'status-action',
};

export async function getAppointmentStatusSummary(params?: {
    branch_id?: number;
    staff_id?: number;
    telesale_id?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
}, scope: AppointmentStatusScope = 'appointment'): Promise<AppointmentStatusSummaryResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.branch_id !== undefined) query.append('branch_id', String(params.branch_id));
        if (params?.staff_id !== undefined) query.append('staff_id', String(params.staff_id));
        if (params?.telesale_id !== undefined) query.append('telesale_id', String(params.telesale_id));
        if (params?.search) query.append('search', params.search);
        if (params?.date_from) query.append('date_from', params.date_from);
        if (params?.date_to) query.append('date_to', params.date_to);
        const qs = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/appointments/summary/${STATUS_SUMMARY_PATH[scope]}${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        return {
            status: Boolean(json?.status),
            data: Array.isArray(json?.data) ? json.data : [],
            msg: json?.msg,
        };
    } catch (error) {
        console.error('Failed to fetch appointment status summary:', error);
        return { status: false, data: [] };
    }
}
